import { type MouseEvent, useCallback, useEffect, useState } from 'react';
import FilterChip from '../data-grid/filters/common/filter-chip.component';
import DateRangeFilterField from '../data-grid/filters/date-range-filter-field.component';
import { isDropDownNoSelection } from '../fields/drop-down/drop-down-common';
import type { DropDownNoSelection, DropDownResult } from '../fields/drop-down/drop-down-types';
import OdinIcon, { OdinIconSize } from '../odin-icon/odin-icon.component';
import { Popover, PopoverContent, PopoverTrigger } from '../popover/popover';
import { getCustomDateRangeItem, getDateRanges } from './date-range-filter-functions';

export interface DateRange {
  from?: Date;
  to?: Date;
}

interface DateRangeFilterProps {
  value?: DateRange;
  selectedQuickDateRange?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplyFilter: (dateRange: DateRange, quickDateRange?: DropDownResult) => void;
  onClearFilter: () => void;
  onHidePanel?: () => void;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}

const DateRangeFilter = ({
  value,
  selectedQuickDateRange,
  open,
  onOpenChange,
  onApplyFilter,
  onClearFilter,
  onHidePanel,
  side = 'bottom',
  align = 'start',
}: DateRangeFilterProps) => {
  const [quickDateRange, setQuickDateRange] = useState<DropDownResult | DropDownNoSelection>();
  const [fromDate, setFromDate] = useState<Date>();
  const [toDate, setToDate] = useState<Date>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only sync when value prop changes, check state to avoid overwriting user changes
  useEffect(() => {
    if (value) {
      if (!fromDate && !toDate && !quickDateRange) {
        setFromDate(value.from);
        setToDate(value.to);
        if (selectedQuickDateRange) {
          const dateRange = getDateRanges().find((range) => range.value === selectedQuickDateRange);
          if (dateRange) {
            setQuickDateRange(dateRange);
          } else {
            setQuickDateRange(getCustomDateRangeItem());
          }
        }
      }
    } else {
      clearFilter();
    }
  }, [value]);

  const clearFilter = () => {
    setFromDate(undefined);
    setToDate(undefined);
    setQuickDateRange(undefined);
    onClearFilter();
  };

  const handleOpenChange = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      onHidePanel?.();
    }
  };

  const handleApplyClick = useCallback(() => {
    if ((fromDate || toDate) && !isDropDownNoSelection(quickDateRange)) {
      onApplyFilter({ from: fromDate, to: toDate }, quickDateRange);
    }
    onHidePanel?.();
  }, [fromDate, toDate, quickDateRange, onApplyFilter, onHidePanel]);

  const handleFilterChipClear = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    clearFilter();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger
        nativeButton={false}
        render={
          <div className="flex w-full items-center border border-gray-4 bg-mono-1 px-1 text-xs">
            <FilterChip text={selectedQuickDateRange} onClear={handleFilterChipClear} />
            <OdinIcon size={OdinIconSize.ExtraSmall} icon="Calendar" />
          </div>
        }
      />
      <PopoverContent side={side} align={align} className="w-[400px] min-w-min max-w-lg">
        <DateRangeFilterField
          fromDate={fromDate}
          toDate={toDate}
          quickDateRange={quickDateRange}
          setFromDate={setFromDate}
          setToDate={setToDate}
          setQuickDateRange={setQuickDateRange}
          clearFilter={clearFilter}
          showApplyButton
          onApplyClick={handleApplyClick}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DateRangeFilter;
